import { useEffect } from "react";

const BASE = "https://tuttimieilink.com";

interface SeoHeadProps {
  title: string;
  description: string;
  path: string;
  noindex?: boolean;
  ogImage?: string;
  ogType?: string;
  ogTitle?: string;
  ogDescription?: string;
}

/**
 * Sets document title, meta description, canonical, OG tags, and Twitter tags dynamically.
 * Runs synchronously on first render to ensure crawlers get correct meta before indexing.
 * Cleans up on unmount to avoid stale meta from SPA navigation.
 */
const SeoHead = ({
  title,
  description,
  path,
  noindex = false,
  ogImage = `${BASE}/logo.jpg`,
  ogType = "website",
  ogTitle,
  ogDescription,
}: SeoHeadProps) => {
  const url = `${BASE}${path}`;
  const finalOgTitle = ogTitle ?? title;
  const finalOgDescription = ogDescription ?? description;

  useEffect(() => {
    document.title = title;

    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        if (selector.includes("name=")) {
          el.setAttribute("name", selector.match(/name="([^"]+)"/)?.[1] || "");
        } else if (selector.includes("property=")) {
          el.setAttribute("property", selector.match(/property="([^"]+)"/)?.[1] || "");
        }
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[name="robots"]', "content", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");

    // Open Graph
    setMeta('meta[property="og:title"]', "content", finalOgTitle);
    setMeta('meta[property="og:description"]', "content", finalOgDescription);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:type"]', "content", ogType);
    setMeta('meta[property="og:image"]', "content", ogImage);
    setMeta('meta[property="og:image:width"]', "content", "1280");
    setMeta('meta[property="og:image:height"]', "content", "720");
    setMeta('meta[property="og:image:alt"]', "content", finalOgTitle);
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");

    // Twitter
    setMeta('meta[name="twitter:title"]', "content", finalOgTitle);
    setMeta('meta[name="twitter:description"]', "content", finalOgDescription);
    setMeta('meta[name="twitter:image"]', "content", ogImage);
    setMeta('meta[name="twitter:image:alt"]', "content", finalOgTitle);

    return () => {
      document.title = "TuttiIMieiLink - Link in Bio per Vendere | +40% Conversioni";
      // Reset canonical to homepage on unmount
      if (canonical) canonical.setAttribute("href", `${BASE}/`);
    };
  }, [title, description, url, noindex, ogImage, ogType, finalOgTitle, finalOgDescription]);

  return null;
};

export default SeoHead;